import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import type { ExportResource } from '@/components/exports/types';
import axiosInstance from '@/libs/axiosInstance';
import {
  parseFilenameFromDisposition,
  parseJsonFromBlob,
  triggerBlobDownload,
} from '@/libs/download';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import useExportsStore from '@/stores/exports-store';
import type {
  ApiErrorResponse,
  ApiResponse,
  CreateExportRequest,
  ExportAsyncResponseData,
} from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';

type RawExportResponse = AxiosResponse<Blob>;

/** Arguments for a single export request. */
export interface ExportActionArgs {
  resource: ExportResource;
  filters: NonNullable<CreateExportRequest['filters']>;
}

/** Result returned from the mutation. */
export interface ExportActionResult {
  mode: 'sync' | 'async';
  exportId?: string;
  rowCount?: number;
  fileName?: string;
}

interface UseExportActionOptions {
  onSuccess?: (result: ExportActionResult) => void;
  onError?: (error: Error) => void;
}

function getResponseHeader(response: RawExportResponse, name: string): string | undefined {
  return (
    (response.headers[name.toLowerCase()] as string | undefined) ??
    (response.headers[name] as string | undefined)
  );
}

function getFallbackExtension(
  contentType: string,
  filters: Record<string, unknown>
): 'csv' | 'xlsx' {
  const normalizedContentType = contentType.toLowerCase();

  if (
    filters.format === 'xlsx' ||
    normalizedContentType.includes('spreadsheetml.sheet') ||
    normalizedContentType.includes('application/vnd.ms-excel')
  ) {
    return 'xlsx';
  }

  return 'csv';
}

function isDownloadableExportResponse(response: RawExportResponse): boolean {
  const contentType = (getResponseHeader(response, 'content-type') ?? '').toLowerCase();
  const disposition = (getResponseHeader(response, 'content-disposition') ?? '').toLowerCase();

  if (contentType.includes('application/json') || contentType.includes('+json')) {
    return false;
  }

  return (
    contentType.includes('text/csv') ||
    contentType.includes('spreadsheetml.sheet') ||
    contentType.includes('application/vnd.ms-excel') ||
    contentType.includes('application/octet-stream') ||
    disposition.includes('attachment')
  );
}

function buildFallbackFileName(resource: ExportResource, extension: 'csv' | 'xlsx'): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `export_${resource}_${stamp}.${extension}`;
}

/**
 * Generic export mutation. Posts resource + filters, then either streams a
 * direct download (sync path) or registers an async job with the shared
 * exports-store so `<ExportProgressToast />` can display progress.
 */
export const useExportAction = (options?: UseExportActionOptions) => {
  const { t } = useTranslation();
  const upsertExport = useExportsStore((s) => s.upsert);

  const mutation = useMutation<ExportActionResult, AxiosError<ApiErrorResponse>, ExportActionArgs>({
    mutationFn: async ({ resource, filters }) => {
      return trackProductAction('exports', 'request', async () => {
        const payload: CreateExportRequest = { resource, filters };

        const response: RawExportResponse = await axiosInstance.post(
          '/dashboard/exports',
          payload,
          {
            responseType: 'blob',
          }
        );

        const contentType = getResponseHeader(response, 'content-type') ?? '';

        if (isDownloadableExportResponse(response)) {
          // Sync path: stream download.
          const rowCountHeader = getResponseHeader(response, 'x-export-row-count');
          const rowCount = rowCountHeader ? Number.parseInt(rowCountHeader, 10) : undefined;

          const dispositionHeader = getResponseHeader(response, 'content-disposition');
          const fallbackExtension = getFallbackExtension(contentType, filters);
          const fileName =
            parseFilenameFromDisposition(dispositionHeader) ??
            buildFallbackFileName(resource, fallbackExtension);

          triggerBlobDownload(response.data, fileName);

          return {
            mode: 'sync' as const,
            rowCount,
            fileName,
          };
        }

        // Async path: backend returned a JSON envelope wrapped in a blob.
        const envelope = await parseJsonFromBlob<ApiResponse<ExportAsyncResponseData>>(
          response.data
        );

        const data = envelope.data;
        if (!data?.exportId) {
          throw new Error('Invalid async export response: missing exportId');
        }

        upsertExport({
          exportId: data.exportId,
          resource,
          status: 'pending',
          createdAt: Date.now(),
        });

        return {
          mode: 'async' as const,
          exportId: data.exportId,
        };
      });
    },

    onSuccess: (result) => {
      if (result.mode === 'sync') {
        // Empty result: treat as informational instead of a plain success.
        if (result.rowCount === 0) {
          toaster.create({
            title: t('exports.empty'),
            type: 'info',
            duration: 5000,
          });
        } else {
          toaster.create({
            title: t('exports.ready'),
            type: 'success',
            duration: 4000,
          });
        }
      } else {
        toaster.create({
          title: t('exports.pending'),
          type: 'loading',
          duration: 6000,
        });
      }

      options?.onSuccess?.(result);
    },

    onError: (error) => {
      const description = getApiErrorMessage(error, t, 'exports.failed');
      toaster.create({
        title: t('exports.failed'),
        description,
        type: 'error',
        duration: 6000,
      });

      options?.onError?.(error);
    },
  });

  return {
    triggerExport: mutation.mutate,
    isExporting: mutation.isPending,
  };
};
